// A buddy session in the page (docs/COLLABORATION_DESIGN.md): the room's
// socket (web/server/lib/rooms.mjs), its Yjs document, and the project kept
// in step with it.
//
// The page never makes the document itself: it starts empty and takes the
// room's, so that every page has the same one. While the session lasts, the
// room's document is the truth: the project here follows it, and each
// change made here, in the editor (binding.js) or to the project's files,
// goes to the room. The editor's documents of shared files follow their
// Y.Texts at once; the project's files follow a moment later.
//
// Events: welcome, key (the page's rejoin key, to keep), synced, people,
// presence, said, run, links, options, status (connecting, open,
// reconnecting, closed), refused, removed, ended, error, files (the project's
// files changed), meta (its name, main file or mode changed), and a shared
// world's (buddies/world.js).

import * as Y from "yjs";
import * as syncProtocol from "y-protocols/sync";
import * as awarenessProtocol from "y-protocols/awareness";
import * as encoding from "lib0/encoding";
import * as decoding from "lib0/decoding";
import { applyChanges, deltaChanges, replaceText } from "./binding.js";
import { hashBlob } from "../cloud.js";

const MESSAGE_SYNC = 0, MESSAGE_AWARENESS = 1;
// The origin of changes that come from the room.
const ROOM = "room";
// Larger text files are shared as files, not as text (rooms.mjs's textBytes).
const TEXT_LIMIT = 4 << 20;
const WRITE_DELAY_MS = 250, PRESENCE_MS = 100;

// Text as the room keeps it: "\n" line breaks.
export const roomText = (text) => text.replace(/\r\n?/g, "\n");

// An invite link's room and token, from a page's fragment: #join=<room>.<token>.
export function parseInvite(hash) {
  const value = new URLSearchParams(hash.replace(/^#/, "")).get("join");
  if (!value) return null;
  const dot = value.indexOf(".");
  return dot > 0 ? { room: value.slice(0, dot), token: value.slice(dot + 1) } : { room: value, token: "" };
}

export const inviteLink = (room, token) => `${location.origin}${location.pathname}#join=${room}.${token}`;

// Whether a project may take a file at path from the room: anyone in a
// session can name a file, and the host's project may be a folder on their
// disk, so no hidden files or folders (such as .git, whose files can run
// commands), nothing outside the project, and no names Windows keeps for
// itself or cannot hold.
export function safeRoomPath(path) {
  if (typeof path !== "string" || !path || path.length > 240) return false;
  if (/[\\:*?"<>|\u0000-\u001f]/.test(path)) return false;
  return path.split("/").every((part) => part && !part.startsWith(".") && !/[ .]$/.test(part) &&
    !/^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\.|$)/i.test(part));
}

// Whether the room's files map has had a file at path, though it may have
// been removed since: Yjs keeps a removed key's entry, without its content.
function roomHad(files, path) {
  return files.has(path) || !!(files._map && files._map.has(path));
}

export class BuddySession extends EventTarget {
  // isText(path): whether the editor opens a file as text. key: the rejoin
  // key the room gave this person's page before (event key), which brings
  // them back as they were, even once the host has made new links.
  constructor({ cloud, room, token = "", as = undefined, key = "", isText }) {
    super();
    this.cloud = cloud;
    this.roomId = room;
    this.token = token;
    this.as = as;
    this.key = key || "";
    this.isText = isText;
    this.doc = new Y.Doc();
    this.files = this.doc.getMap("files");
    this.meta = this.doc.getMap("meta");
    this.awareness = new awarenessProtocol.Awareness(this.doc);
    this.you = null;
    this.room = null;
    this.people = [];
    this.messages = [];
    this.state = "connecting";
    this.stopped = false;           // ended, removed, refused or left: no reconnecting
    this.synced = false;
    this.retries = 0;
    this.project = null;
    this.editor = null;
    this.writes = new Map();        // path -> timer, for the room's changes on their way to the project
    this.writing = new Map();       // path -> the write under way
    this.unsafe = new Set();        // paths the room has that are not written here (safeRoomPath)
    this.uploaded = new Set();      // hashes of the files this page has sent the room (upload)
    this.ready = new Promise((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });
    this.ready.catch(() => {});
    this.doc.on("update", (update, origin) => {
      if (origin !== ROOM) this.sendSync((encoder) => syncProtocol.writeUpdate(encoder, update));
    });
    this.awareness.on("update", ({ added, updated, removed }, origin) => {
      if (origin !== ROOM) this.sendAwareness([...added, ...updated, ...removed]);
      this.emit("presence");
    });
    this.files.observeDeep((events, transaction) => this.filesChanged(events, transaction));
    this.meta.observe((event, transaction) => {
      if (transaction.origin === ROOM) this.metaChanged();
    });
    this.open();
  }

  emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  get role() { return this.you ? this.you.role : null; }
  get canEdit() { return this.role === "host" || this.role === "editor"; }
  get isHost() { return this.role === "host"; }
  get name() { return this.meta.get("name") || (this.room && this.room.name) || "Buddy session"; }

  // The socket, and a new one after it drops, until the session stops.
  open() {
    const url = new URL(`rooms/${this.roomId}/socket`, this.cloud.base);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const ws = this.ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    ws.onopen = () => ws.send(JSON.stringify({ type: "join", token: this.token, as: this.as,
      ...(this.key ? { key: this.key } : {}) }));
    ws.onmessage = (event) => {
      if (ws !== this.ws) return;
      try {
        if (typeof event.data === "string") this.json(JSON.parse(event.data));
        else this.binary(new Uint8Array(event.data));
      } catch (error) {
        console.error("Buddy session:", error);
      }
    };
    ws.onclose = () => {
      if (ws !== this.ws) return;
      this.joined = false;
      if (this.stopped) {
        this.setState("closed");
        return;
      }
      // Reconnects, sooner at first.
      this.setState("reconnecting");
      const delay = Math.min(10000, 500 * 2 ** this.retries++);
      this.retryTimer = setTimeout(() => {
        if (!this.stopped) this.open();
      }, delay);
    };
  }

  setState(state) {
    this.state = state;
    this.emit("status", state);
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.joined) this.ws.send(message);
  }

  sendJson(value) {
    this.send(JSON.stringify(value));
  }

  sendSync(write) {
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_SYNC);
    write(encoder);
    this.send(encoding.toUint8Array(encoder));
  }

  sendAwareness(clients) {
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_AWARENESS);
    encoding.writeVarUint8Array(encoder, awarenessProtocol.encodeAwarenessUpdate(this.awareness, clients));
    this.send(encoding.toUint8Array(encoder));
  }

  json(message) {
    switch (message.type) {
      case "welcome":
        this.you = message.you;
        this.room = message.room;
        this.people = message.people;
        this.messages = message.messages || [];
        this.joined = true;
        this.retries = 0;
        if (message.you && typeof message.you.key === "string" && message.you.key !== this.key) {
          this.key = message.you.key;
          this.emit("key", this.key);
        }
        this.setState("open");
        this.emit("welcome");
        // The room's document, and what this page has that the room has not.
        this.sendSync((encoder) => syncProtocol.writeSyncStep1(encoder, this.doc));
        // Presence again, as new: the room forgot this page's when it left.
        if (this.awareness.getLocalState()) this.awareness.setLocalState({ ...this.awareness.getLocalState() });
        break;
      case "people":
        this.people = message.people;
        this.emit("people");
        this.emit("presence");
        break;
      case "said":
        this.messages.push(message.message);
        this.emit("said", message.message);
        break;
      case "run":
        this.emit("run", message);
        break;
      case "links":
        if (this.room) this.room.links = message.links;
        this.emit("links");
        break;
      case "options":
        if (this.room) this.room.allowAnonymous = message.allowAnonymous;
        this.emit("options");
        break;
      case "refused":
        this.stopped = true;
        this.rejectReady(Object.assign(new Error(message.message), { reason: message.reason }));
        this.emit("refused", message);
        break;
      case "removed":
        this.stopped = true;
        this.emit("removed");
        break;
      case "ended":
        this.stopped = true;
        this.emit("ended");
        break;
      case "error":
        this.emit("error", message.message);
        break;
      case "world-state":
      case "world-edit":
      case "world-refused":
      case "world-resync":
      case "world-saved":
      case "world-checkpoint-wanted":
        this.emit(message.type, message);
        break;
      case "blip-turn":
        if (this.blipWaiting) {
          this.blipWaiting.resolve();
          this.blipWaiting = null;
        }
        break;
      case "blip-state":
        this.blipState = message;
        this.emit("blip-state", message);
        break;
      case "pictures-removed": {
        const said = this.messages.find((entry) => entry.id === message.id);
        if (said) delete said.pictures;
        this.emit("pictures-removed", message);
        break;
      }
      case "activity":
        this.emit("activity", message);
        break;
    }
  }

  // Blip takes one request at a time for the room. Resolves when it is this
  // person's turn; the question goes to everyone's log, with the pictures
  // shown with it ([{ hash, type, width, height }], files of the room's). A
  // turn ends with blipDone, with his answer for the log, or with nothing to
  // give it up.
  blipTurn(text, pictures = []) {
    if (this.blipWaiting) this.blipWaiting.reject(new Error("Asked again"));
    return new Promise((resolve, reject) => {
      this.blipWaiting = { resolve, reject };
      this.sendJson({ type: "blip-ask", text, ...(pictures.length ? { pictures } : {}) });
    });
  }

  // The host's: takes a question's pictures out, for everyone.
  removePictures(id) {
    this.sendJson({ type: "pictures-remove", id });
  }

  blipDone(reply = "") {
    if (this.blipWaiting) {
      this.blipWaiting.reject(new Error("Stopped"));
      this.blipWaiting = null;
    }
    this.sendJson({ type: "blip-done", reply });
  }

  // Who changed which files since a time: [{ time, name, files }].
  activitySince(since) {
    return new Promise((resolve) => {
      const answered = (event) => {
        this.removeEventListener("activity", answered);
        resolve(event.detail.entries);
      };
      this.addEventListener("activity", answered);
      this.sendJson({ type: "activity", since });
    });
  }

  // The shared text files as they are now, for Catch me up to compare with
  // later.
  takeSnapshot() {
    this.snapshot = new Map();
    for (const [path, value] of this.files.entries()) if (value instanceof Y.Text) this.snapshot.set(path, value.toString());
    this.lookedAt = Date.now();
  }

  // Shared worlds (docs/COLLABORATION_DESIGN.md, "World transactions"):
  // the World Editor's edits go through the room, which puts them in one
  // order. openWorld answers with the world's latest checkpoint and the edits
  // after it: { seq, base: { seq, text, hash }, log }.
  openWorld(world) {
    return new Promise((resolve) => {
      const answered = (event) => {
        if (event.detail.world !== world) return;
        this.removeEventListener("world-state", answered);
        resolve(event.detail);
      };
      this.addEventListener("world-state", answered);
      this.sendJson({ type: "world-open", world });
    });
  }

  // An edit made here: { world, base, id, label, touches, operations }. The
  // room answers with world-edit, the acknowledgement, or world-refused.
  sendWorldEdit(edit) {
    this.sendJson({ type: "world-edit", ...edit });
  }

  // The world at the room's edit seq: { seq, hash, text or blob (the hash of
  // a file sent with upload), saved }.
  sendWorldCheckpoint(world, checkpoint) {
    this.sendJson({ type: "world-checkpoint", world, ...checkpoint });
  }

  sendWorldHash(world, seq, hash) {
    this.sendJson({ type: "world-hash", world, seq, hash });
  }

  // The World Editor here left the world.
  sendWorldClose(world) {
    this.sendJson({ type: "world-close", world });
  }

  // The room's edit seq could not be applied here.
  sendWorldBroken(world, seq) {
    this.sendJson({ type: "world-broken", world, seq });
  }

  // The World Editor here is about to save the world's file with text whose
  // hash this is.
  sendWorldSaving(world, hash) {
    this.sendJson({ type: "world-saving", world, hash });
  }

  // Whether the room has a file at path.
  hasFile(path) {
    return this.files.has(path);
  }

  // A world the room keeps as a file of its own, by its hash, as text.
  async worldText(hash) {
    return (await this.download(hash)).text();
  }

  binary(data) {
    const decoder = decoding.createDecoder(data);
    const type = decoding.readVarUint(decoder);
    if (type === MESSAGE_SYNC) {
      const encoder = encoding.createEncoder();
      encoding.writeVarUint(encoder, MESSAGE_SYNC);
      const step = syncProtocol.readSyncMessage(decoder, encoder, this.doc, ROOM);
      if (encoding.length(encoder) > 1) this.send(encoding.toUint8Array(encoder));
      if (step === syncProtocol.messageYjsSyncStep2 && !this.synced) {
        this.synced = true;
        this.resolveReady();
        this.emit("synced");
      }
    } else if (type === MESSAGE_AWARENESS) {
      awarenessProtocol.applyAwarenessUpdate(this.awareness, decoding.readVarUint8Array(decoder), ROOM);
    }
  }

  // Leaves the session: the socket closes, and nothing more is sent.
  close() {
    this.stopped = true;
    if (this.blipWaiting) {
      this.blipWaiting.reject(new Error("The session closed"));
      this.blipWaiting = null;
    }
    clearTimeout(this.retryTimer);
    clearTimeout(this.presenceTimer);
    for (const timer of this.writes.values()) clearTimeout(timer);
    this.writes.clear();
    this.detach();
    if (this.ws) this.ws.close();
    this.awareness.destroy();
  }

  // The shared text of a file, or null for a file shared as a file.
  textOf(path) {
    const value = this.files.get(path);
    return value instanceof Y.Text ? value.toString() : null;
  }

  // Messages to the room.
  say(text) {
    this.sendJson({ type: "say", text });
  }

  // The project here, kept in step with the room's document. editor is the
  // program editor (editor.js); ide: { refreshFiles(), closeFile(path),
  // metaChanged() }.
  async attach(project, editor, ide) {
    this.detach();
    this.project = project;
    this.editor = editor;
    this.ide = ide;
    // Writes that come from the room go straight to the project; the
    // project's own methods also pass their changes to the room.
    const raw = this.raw = { write: project.write, remove: project.remove, update: project.update };
    // whole: the file is written whole, as a tool such as the World Editor
    // saves one, and replaces the room's at once (pushFile).
    project.write = async (path, contents, { whole = false } = {}) => {
      // The editor's own saves hold its text, which the room already has.
      const editorText = editor.textOf(path);
      const written = await raw.write.call(project, path, contents);
      // The file is written here even if the room cannot take it.
      try {
        await this.pushFile(written, contents, editorText, whole);
      } catch (error) {
        this.emit("error", `${written} could not be shared: ${error.message}`);
      }
      return written;
    };
    project.remove = async (path) => {
      await raw.remove.call(project, path);
      if (this.canEdit && this.files.has(path)) this.doc.transact(() => this.files.delete(path));
    };
    project.update = async (changes) => {
      await raw.update.call(project, changes);
      this.pushMeta(changes);
    };
    editor.share({
      textOf: (path) => this.textOf(path),
      local: (path, changes) => this.localChange(path, changes),
      selection: (path, range) => this.localSelection(path, range),
    });
    editor.setReadOnly(!this.canEdit);
    await this.reconcile();
    this.takeSnapshot();
    // Editor documents opened before the session take its text.
    for (const path of this.files.keys()) {
      const shared = this.textOf(path), shown = editor.textOf(path);
      if (shared !== null && shown !== undefined && shown !== shared) editor.applyRemote(path, [], shared);
    }
  }

  detach() {
    if (!this.project) return;
    const { project, raw } = this;
    project.write = raw.write;
    project.remove = raw.remove;
    project.update = raw.update;
    if (this.editor) this.editor.share(null);
    this.project = null;
    this.editor = null;
  }

  // Makes the project here hold what the room's document holds.
  async reconcile() {
    const project = this.project;
    for (const path of [...this.files.keys()]) await this.writeFromRoom(path);
    for (const path of project.paths()) {
      if (this.files.has(path) || this.project !== project) continue;
      // A file the room had, and someone removed, goes. One it never had,
      // such as a file put in the host's folder outside the IDE, or one whose
      // upload failed, is the host's: it joins the room, and is never lost.
      if (this.isHost && !roomHad(this.files, path)) await this.pushFile(path).catch(() => {});
      else if (!this.isHost) await this.raw.remove.call(project, path);
    }
    await this.metaChanged(false);
    if (this.ide) this.ide.refreshFiles();
  }

  // A file as the room has it, written to the project unless it is the same.
  // Paths a project must not take from others are passed over (safeRoomPath).
  async writeFromRoom(path) {
    const project = this.project;
    if (!project) return;
    if (!safeRoomPath(path)) {
      if (!this.unsafe.has(path)) {
        this.unsafe.add(path);
        this.emit("error", `A file named ${JSON.stringify(path.slice(0, 80))} was not brought in: the session cannot ` +
          "make hidden files, or names Windows keeps for itself.");
      }
      return;
    }
    const previous = this.writing.get(path) || Promise.resolve();
    const writing = previous.then(async () => {
      if (this.project !== project) return;
      const value = this.files.get(path);
      if (value === undefined) {
        if (project.has(path)) {
          await this.raw.remove.call(project, path);
          if (this.ide) this.ide.closeFile(path);
        }
        return;
      }
      if (value instanceof Y.Text) {
        const text = value.toString();
        if (project.has(path) && roomText(await project.text(path)) === text) return;
        await this.raw.write.call(project, path, text);
        // A change while this was written is written next.
        if (value.toString() !== text) this.queueWrite(path);
      } else if (value && typeof value.hash === "string") {
        if (project.has(path) && await hashBlob(project.blob(path)) === value.hash) return;
        const blob = await this.download(value.hash);
        if (blob && this.project === project && this.files.get(path) === value) await this.raw.write.call(project, path, blob);
      }
    }).catch((error) => this.emit("error", `${path} could not be brought in: ${error.message}`));
    this.writing.set(path, writing);
    await writing;
    if (this.writing.get(path) === writing) this.writing.delete(path);
  }

  queueWrite(path) {
    clearTimeout(this.writes.get(path));
    this.writes.set(path, setTimeout(async () => {
      this.writes.delete(path);
      await this.writeFromRoom(path);
      if (this.ide) this.ide.refreshFiles();
    }, WRITE_DELAY_MS));
  }

  // Waits for the room's changes to reach the project.
  async settle() {
    for (const [path, timer] of [...this.writes]) {
      clearTimeout(timer);
      this.writes.delete(path);
      await this.writeFromRoom(path);
    }
    await Promise.all(this.writing.values());
  }

  // The room's changes to the files: shown at once in the editor, and in the
  // project a moment later.
  filesChanged(events, transaction) {
    if (transaction.origin !== ROOM) return;
    const changed = new Set();
    for (const event of events) {
      if (event.target === this.files) {
        for (const [path] of event.changes.keys) {
          changed.add(path);
          const value = this.files.get(path);
          if (this.editor && value instanceof Y.Text) this.editor.applyRemote(path, [], value.toString());
        }
      } else if (event.target instanceof Y.Text) {
        const path = event.target._item && event.target._item.parentSub;
        if (!path || this.files.get(path) !== event.target) continue;
        if (this.editor) this.editor.applyRemote(path, deltaChanges(event.delta), event.target.toString());
        changed.add(path);
      }
    }
    for (const path of changed) this.queueWrite(path);
    this.emit("files", [...changed]);
  }

  // The project's name, main file and mode as the room has them.
  async metaChanged(notify = true) {
    const project = this.project;
    if (!project) return;
    const changes = {};
    for (const key of ["name", "main", "mode"]) {
      const value = this.meta.get(key);
      if (value !== undefined && value !== project.meta[key]) changes[key] = value;
    }
    if (Object.keys(changes).length) await this.raw.update.call(project, changes);
    if (notify && this.ide) this.ide.metaChanged();
  }

  pushMeta(changes) {
    if (!this.canEdit) return;
    const keys = ["name", "main", "mode"].filter((key) => key in changes && changes[key] !== this.meta.get(key));
    if (keys.length) this.doc.transact(() => { for (const key of keys) this.meta.set(key, changes[key]); });
  }

  // A file written to the project here, passed to the room. editorText is
  // the editor's text of the file when the write began: a write of that text
  // is the editor's own save, whose changes the room already has. A text file
  // written whole replaces the room's with a new text, rather than being
  // merged into it as an edit: two people saving one world at the same moment
  // would otherwise leave both saves in its file, and the room keeps one.
  async pushFile(path, contents, editorText, whole = false) {
    if (!this.canEdit || !this.project) return;
    const blob = this.project.blob(path);
    if (!blob) return;
    if (this.isText(path) && blob.size <= TEXT_LIMIT) {
      const text = roomText(typeof contents === "string" ? contents : await blob.text());
      if (editorText !== undefined && editorText === text) return;
      const value = this.files.get(path);
      if (value instanceof Y.Text) {
        if (value.toString() === text) return;
        if (whole) this.doc.transact(() => this.files.set(path, new Y.Text(text)));
        else this.doc.transact(() => replaceText(value, text));
      } else {
        this.doc.transact(() => this.files.set(path, new Y.Text(text)));
      }
      // The editor shows what was written.
      if (this.editor && this.editor.textOf(path) !== undefined && this.editor.textOf(path) !== text) {
        this.editor.applyRemote(path, [], text);
      }
      return;
    }
    const hash = await hashBlob(blob);
    const value = this.files.get(path);
    if (value && !(value instanceof Y.Text) && value.hash === hash) return;
    await this.upload(hash, blob);
    this.doc.transact(() => this.files.set(path, { hash, size: blob.size }));
  }

  // Typing in a shared file's document goes to its Y.Text. A text file not
  // yet shared as text becomes so.
  localChange(path, changes) {
    if (!this.canEdit) return;
    const value = this.files.get(path);
    if (value instanceof Y.Text) {
      this.doc.transact(() => applyChanges(value, changes));
    } else if (this.project && this.project.has(path) && this.editor) {
      const text = this.editor.textOf(path);
      if (text !== undefined && text.length <= TEXT_LIMIT) this.doc.transact(() => this.files.set(path, new Y.Text(text)));
    }
    this.lastTyped = Date.now();
  }

  // Where this person is: the file shown and its cursor, by positions in its
  // Y.Text that move with others' changes.
  localSelection(path, range) {
    const value = this.files.get(path);
    const position = (index) => value instanceof Y.Text ?
      Y.relativePositionToJSON(Y.createRelativePositionFromTypeIndex(value, index)) : null;
    this.setPresence({ file: path, anchor: position(range.anchor), head: position(range.head),
      typed: this.lastTyped || 0 });
  }

  // The file shown, when it changes without a cursor move.
  fileShown(path) {
    const state = this.awareness.getLocalState() || {};
    if (state.file !== path) this.setPresence({ file: path, anchor: null, head: null });
  }

  // Presence goes out at most ten times a second.
  setPresence(fields) {
    this.presence = { ...(this.presence || this.awareness.getLocalState() || {}), ...fields };
    if (this.presenceTimer) return;
    this.presenceTimer = setTimeout(() => {
      this.presenceTimer = 0;
      this.awareness.setLocalState(this.presence);
    }, PRESENCE_MS);
  }

  // A person's presence: their latest awareness state.
  presenceOf(person) {
    const states = this.awareness.getStates();
    for (const client of [...(person.clients || [])].reverse()) if (states.has(client)) return states.get(client);
    return null;
  }

  // The others' cursors in a file: [{ id, from, to, head, colour, name, recent }].
  cursorsIn(path) {
    const value = this.files.get(path);
    if (!(value instanceof Y.Text)) return [];
    const cursors = [];
    const index = (json) => {
      if (!json) return null;
      const absolute = Y.createAbsolutePositionFromRelativePosition(Y.createRelativePositionFromJSON(json), this.doc);
      return absolute && absolute.type === value ? absolute.index : null;
    };
    for (const person of this.people) {
      if (this.you && person.id === this.you.id) continue;
      const state = this.presenceOf(person);
      if (!state || state.file !== path) continue;
      const head = index(state.head), anchor = index(state.anchor);
      if (head === null) continue;
      const from = Math.min(head, anchor ?? head), to = Math.max(head, anchor ?? head);
      // While Blip works for someone, his edits are theirs, and the cursor says so.
      cursors.push({ id: person.id, from, to, head, colour: person.colour,
        name: state.blip ? `Blip (asked by ${person.name})` : person.name,
        recent: !!state.blip || Date.now() - (state.typed || 0) < 3000 });
    }
    return cursors;
  }

  // Which files the others have open: Map path -> [colour, ...].
  openFiles() {
    const files = new Map();
    for (const person of this.people) {
      if (this.you && person.id === this.you.id) continue;
      const state = this.presenceOf(person);
      if (!state || !state.file) continue;
      if (!files.has(state.file)) files.set(state.file, []);
      files.get(state.file).push({ colour: person.colour, name: person.name });
    }
    return files;
  }

  // The room's files by path: a file shared as text, as text; another, by
  // its hash.
  headers() {
    return { "X-B3D-IDE": "1", "X-B3D-Room-Token": this.token || "", "X-B3D-Room-Key": this.key || "" };
  }

  async download(hash) {
    const response = await fetch(new URL(`rooms/${this.roomId}/blobs/${hash}`, this.cloud.base),
      { credentials: "same-origin", headers: this.headers() });
    if (!response.ok) throw new Error(`the room answered ${response.status}`);
    return response.blob();
  }

  // Sends a file's bytes to the room, once a page: the room keeps what it was
  // sent for the rest of the session (its record's allowed hashes), so Run
  // for everyone sends only what changed.
  async upload(hash, blob) {
    if (this.uploaded.has(hash)) return;
    const response = await fetch(new URL(`rooms/${this.roomId}/blobs/${hash}`, this.cloud.base),
      { method: "PUT", credentials: "same-origin", headers: this.headers(), body: blob });
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.error || `The room answered ${response.status}`);
    }
    this.uploaded.add(hash);
  }
}
